<template>
  <fts-modal
    append-to-body
    :width="width"
    :header="false"
    class="fts-confirmation"
    @close="dismiss"
    v-if="isShow"
  >
    <!-- body -->
    <template #body>
      <slot name="body">
        <!-- slot:content header -->
        <slot name="contentHeader"></slot>
        <!-- content -->
        <div class="confirmation-content">
          <!-- icon -->
          <span 
            class="confirmation-icon confirmation-icon--material"
            :class="iconPack" 
            :style="{ 'color': iconColor }" 
            v-if="isMaterialIcon"
          >{{icon}}</span>
          <!-- message -->
          <div class="confirmation-message">
            <!-- !isLite and type:delete -->
            <template v-if="!isLite && type === 'delete'">
              <div class="confirmation-title">
                Delete<template v-if="title"> {{title}}</template>?
              </div>

              <div class="confirmation-text">
                <span>Permanently delete</span>
                <div class="confirmation-text-icon" v-if="getTextIcon">
                  <i :class="getTextIcon" />
                </div>
                <div class="confirmation-text-icon" v-if="textEmoji">
                  <fts-moji :size="14" :emoji="textEmoji" />
                </div>
                <span class="confirmation-text-label" :style="getLabelStyle" v-if="textHTML" v-html="textHTML"></span>
                <span class="confirmation-text-label" :style="getLabelStyle" v-else-if="text">{{text}}</span>
                <span>. This cannot be undone.</span>
              </div>
            </template>
            <!-- type:default -->
            <template v-else>
              <div class="confirmation-title">{{title}}</div>
              <div class="confirmation-text" v-if="textHTML" v-html="textHTML"></div>
              <div class="confirmation-text" v-else>{{text}}</div>
            </template>
          </div>
        </div>
        <!-- type:prompt -->
        <div class="confirmation-key-group" v-if="type === 'prompt'">
          <div class="confirmation-key-label">Write description.</div>
          <input
            type="text"
            class="fts-form-control confirmation-form-control"
            v-model="formData.promptValue"
          />
        </div>
        <!-- confirmation key -->
        <div class="confirmation-key-group" v-if="enableConfirmationKey">
          <div class="confirmation-key-label">Type <span>{{confirmationKey}}</span> to confirm.</div>
          <input
            type="text"
            class="fts-form-control confirmation-form-control"
            v-model="formData.confirmationKey"
            :disabled="loading"
          />
        </div>
        <!-- slot:content footer -->
        <slot name="contentFooter"></slot>
      </slot>
    </template>
    <!-- footer --> 
    <template #footer>
      <div class="confirmation-footer">
        <fts-btn
          v-for="(item, ind) in buttons"
          :key="`confirmation-btn-${ind}`"
          v-bind="btnBind(item.bind, item.default)"
          :disabled="
            (!item.default && loading) || 
            (item.default && invalidConfirmationKey) ||
            (item.default && invalidPromptValue)
          "
          :loading="item.default && loading"
          @click="execute(item)"
        >
          {{item.title || '(undefined)'}}
        </fts-btn>
      </div>
    </template>
  </fts-modal>
</template>

<script>
import Vue from 'vue';

import FTSModal from '../FTSModal';
import FTSBtn from '../FTSBtn';
import FTSMoji from '../FTSMoji';
import commons from '../../commons/commons';

export default {
  props: {
    global: Boolean,
    width: {
      type: String,
      default: '425px'
    }
  },
  components: {
    "fts-modal": FTSModal,
    "fts-btn": FTSBtn,
    "fts-moji": FTSMoji
  },
  data: () => ({
    isShow: false,
    resolve: null,
    loading: false,
    /* settings */
    type: null,
    title: null,
    text: null,
    textHTML: null,
    textIcon: null, /* fts-icon-* and information type */
    textEmoji: null,
    textColor: null,
    iconPack: null,
    icon: null,
    iconColor: null,
    backgroundColor: null,
    buttons: [
      {
        title: 'Cancel',
        bind: { 'text': true }
      },
      {
        title: 'Yes',
        default: true,
        handler: ({ resolve }) => resolve(true)
      }
    ],
    enableConfirmationKey: false,
    confirmationKey: 'confirm',
    promptValueRequired: true,
    isLite: false,
    /* formData */
    formData: {}
  }),
  methods: {
    async show(payload = {}) {
      const {
        type,
        isLite,
        title,
        text,
        textHTML,
        textIcon,
        textEmoji,
        textColor,
        iconPack,
        icon,
        iconColor,
        backgroundColor,
        buttons,
        enableConfirmationKey,
        confirmationKey
      } = payload;
      // assign basic flags
      if (type) {
        this.type = type;
      }

      if (isLite) {
        this.isLite = true;
      }
      // preset configurations
      if (this.isLite) {
        if (this.type === "delete") {
          this.iconPack = "material-icons-outlined";
          this.icon = "error";
          this.iconColor = "var(--inistate-icon-warning)";
        }

        this.buttons = [
          {
            title: "Cancel",
            bind: { "outline": true, "color": "primary" }
          },
          {
            title: "Confirm",
            default: true,
            handler: ({ resolve }) => resolve(true)
          }
        ];
      } else if (this.type === "delete") {
        this.buttons = [
          {
            title: "Cancel",
            bind: { text: true }
          },
          {
            title: "Delete",
            bind: { color: "danger" },
            default: true,
            handler: ({ resolve }) => resolve(true)
          }
        ];
        this.confirmationKey = "delete";
      } else {
        this.title = "Confirmation";
        this.text = "Are you sure you want to proceed?";
      }
      // override from payload (only if defined)
      Object.entries({
        title,
        text,
        textHTML,
        textIcon,
        textEmoji,
        textColor,
        iconPack,
        icon,
        iconColor,
        backgroundColor,
        buttons,
        enableConfirmationKey,
        confirmationKey
      }).forEach(([key, value]) => {
        if (value != null) {
          this[key] = value;
        }
      });

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.isShow = true;
      });
    },
    dismiss() {
      if (this.loading) return;
      
      this.resolve('dismiss');
      this.reset();
    },
    async execute(item) {
      /* handler */
      if (item.handler && typeof item.handler === 'function') {
        try {
          await item.handler({
            resolve: (val) => {
              if (this.type === 'prompt')
                this.resolve({ res: val, promptValue: this.formData.promptValue });
              else
                this.resolve(val);
            },
            loading: (val) => this.loading = val
          });
        } catch(err) { console.error(err); }
      } else if (item.default === true) {
        this.resolve(true);
      } else {
        this.resolve('dismiss');
      }
      /* reset */
      this.reset();
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    btnBind(bind, isDefault) {
      let defaultBind = {
        'box-shadow-none': true,
        'text-transform-init': true,
      }

      if (isDefault) {
        defaultBind['color'] = 'primary';
      }

      return bind && Object.prototype.toString.call(bind) === "[object Object]" ?
        { ...defaultBind, ...bind } : { ...defaultBind }
    }
  },
  computed: {
    getTextIcon() {
      if (this.textIcon !== null && this.textIcon !== undefined) {
        return commons.isInteger(this.textIcon) ? 
          commons.informationIcon(this.textIcon) : this.textIcon;
      }
    },
    getLabelStyle() {
      let style = {};
      if (this.backgroundColor) {
        style.display = "inline-block";
        style.paddingLeft = "0.375rem";
        style.paddingRight = "0.375rem";
        style.borderRadius = "0.25rem";
        style.backgroundColor = this.backgroundColor;
        if (!this.textColor) {
          style.color = "var(--fts-light)";
        }
      }
      if (this.textColor) {
        style.color = this.textColor;
      }
      return style;
    },
    invalidConfirmationKey() {
      return this.enableConfirmationKey && this.formData.confirmationKey !== this.confirmationKey;
    },
    invalidPromptValue() {
      return this.type === 'prompt' && this.promptValueRequired && !(this.formData.promptValue && this.formData.promptValue.trim());
    },
    isMaterialIcon() {
      if (!this.iconPack || !this.icon) {
        return false;
      }

      return this.iconPack.startsWith("material");
    }
  },
  mounted() {
    if (this.global) {
      Vue.prototype.$confirmation = {
        show: (payload) => this.show(payload)
      };
    }
  }
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-confirmation {
  .confirmation-content {
    display: flex;
    align-items: flex-start;
  }

  .confirmation-icon {
    &--material {
      font-size: 20px;
    }
  }

  .confirmation-message {
    &:not(:first-child) {
      margin-left: 6px;
    }
  }

  .confirmation-title {
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-16);
    line-height: var(--inistate-heading-text-16--line-height);
  }
  .confirmation-text {
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    white-space: pre-line;
    &:not(:first-child) {
      margin-top: 8px;
    }
  }
  .confirmation-text-icon {
    height: 1.25rem;
    display: inline-flex;
    align-items: center;
    margin-left: 0.125rem;
    margin-right: 0.125rem;
    vertical-align: bottom;
    color: var(--fts-default);
    color: var(--fts-secondary);
  }
  .confirmation-text-label {
    font-weight: var(--inistate-font-weight-medium);
  }
  .confirmation-key-group {
    &:not(:first-child) {
      margin-top: 1.25rem;
    }
  }
  .confirmation-key-label {
    margin-bottom: 0.375rem;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    > span {
      font-weight: var(--inistate-font-weight-medium);
    }
  }
  .confirmation-form-control {
    border-radius: var(--inistate-rounded-4);
  }
  .confirmation-footer {
    @include flex-gap($gap: 0.5rem);
    justify-content: flex-end;
  }
}
</style>